import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { saveCase, unsaveCase, checkIfCaseSaved } from '@/lib/api';
import { trackBookmarkAdded, trackBookmarkRemoved, trackPaywallHit } from '@/lib/telemetry';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';
import { Bookmark, BookmarkCheck, Loader2, Lock, Crown, ArrowRight } from 'lucide-react';

export function SaveCaseButton({ 
  caseId, 
  caseData = {}, 
  variant = 'default', // 'default', 'icon', 'outline'
  size = 'default',
  className = '',
  onSaveChange = null 
}) {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isSaved, setIsSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitInfo, setLimitInfo] = useState({ count: 0, limit: 3 });

  // Check if case is saved on mount
  useEffect(() => {
    if (isAuthenticated && caseId) {
      checkSavedStatus();
    } else {
      setChecking(false);
    }
  }, [isAuthenticated, caseId]);

  const checkSavedStatus = async () => {
    try {
      const result = await checkIfCaseSaved(caseId);
      setIsSaved(result.is_saved);
      setLimitInfo({
        count: result.current_count,
        limit: result.limit,
        canSaveMore: result.can_save_more
      });
    } catch (error) {
      console.error('Failed to check saved status:', error);
    } finally {
      setChecking(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      navigate('/login', { state: { from: window.location.pathname } });
      return;
    }

    setLoading(true);
    try {
      if (isSaved) {
        // Unsave
        const result = await unsaveCase(caseId);
        if (result.success) {
          setIsSaved(false);
          setLimitInfo(prev => ({
            ...prev,
            count: result.current_count,
            canSaveMore: result.can_save_more
          }));
          toast.success('Case removed from saved');
          trackBookmarkRemoved(caseId);
          onSaveChange?.(false);
        }
      } else {
        // Save
        const result = await saveCase(caseId, caseData);
        if (result.success) {
          setIsSaved(true);
          setLimitInfo(prev => ({
            ...prev,
            count: result.current_count,
            canSaveMore: result.can_save_more
          }));
          toast.success('Case saved to your workspace');
          trackBookmarkAdded(caseId, {
            current_count: result.current_count,
            limit: result.limit,
          });
          onSaveChange?.(true);
        } else if (result.limit_reached) {
          // Show upgrade modal
          setLimitInfo({
            count: result.current_count,
            limit: result.limit,
            canSaveMore: false
          });
          trackPaywallHit('save_limit', {
            case_id: caseId,
            current_count: result.current_count,
            limit: result.limit,
          });
          setShowLimitModal(true);
        } else if (result.already_saved) {
          setIsSaved(true);
          toast.info('Case already saved');
        }
      }
    } catch (error) {
      console.error('Failed to save/unsave case:', error);
      toast.error('Failed to update saved cases');
    } finally {
      setLoading(false);
    }
  };

  // Icon-only button (for cards)
  if (variant === 'icon') {
    return (
      <>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleSave}
                disabled={loading || checking}
                className={`h-8 w-8 rounded-full ${
                  isSaved 
                    ? 'text-amber-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/30' 
                    : 'text-slate-400 hover:text-amber-500 hover:bg-slate-100 dark:hover:bg-slate-800'
                } ${className}`}
                data-testid={`save-case-btn-${caseId}`}
              >
                {loading || checking ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : isSaved ? (
                  <BookmarkCheck className="h-4 w-4 fill-current" />
                ) : (
                  <Bookmark className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{isSaved ? 'Remove from saved' : 'Save case'}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <LimitReachedModal 
          open={showLimitModal} 
          onOpenChange={setShowLimitModal}
          count={limitInfo.count}
          limit={limitInfo.limit}
        />
      </>
    );
  }

  // Outline button (for case detail page)
  if (variant === 'outline') {
    return (
      <>
        <Button
          variant={isSaved ? 'default' : 'outline'}
          size={size}
          onClick={handleSave}
          disabled={loading || checking}
          className={`rounded-full ${
            isSaved 
              ? 'bg-amber-500 hover:bg-amber-600 text-white' 
              : 'hover:border-amber-400 hover:text-amber-600'
          } ${className}`}
          data-testid={`save-case-btn-${caseId}`}
        >
          {loading || checking ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : isSaved ? (
            <BookmarkCheck className="h-4 w-4 mr-2" />
          ) : (
            <Bookmark className="h-4 w-4 mr-2" />
          )}
          {isSaved ? 'Saved' : 'Save Case'}
        </Button>

        <LimitReachedModal 
          open={showLimitModal} 
          onOpenChange={setShowLimitModal}
          count={limitInfo.count}
          limit={limitInfo.limit}
        />
      </>
    );
  }

  // Default button style
  return (
    <>
      <Button
        variant={isSaved ? 'secondary' : 'default'}
        size={size}
        onClick={handleSave}
        disabled={loading || checking}
        className={`${
          isSaved 
            ? 'bg-amber-100 hover:bg-amber-200 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' 
            : ''
        } ${className}`}
        data-testid={`save-case-btn-${caseId}`}
      >
        {loading || checking ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : isSaved ? (
          <BookmarkCheck className="h-4 w-4 mr-2" />
        ) : (
          <Bookmark className="h-4 w-4 mr-2" />
        )}
        {isSaved ? 'Saved' : 'Save'}
      </Button>

      <LimitReachedModal 
        open={showLimitModal} 
        onOpenChange={setShowLimitModal}
        count={limitInfo.count}
        limit={limitInfo.limit}
      />
    </>
  );
}

// Limit Reached Modal
function LimitReachedModal({ open, onOpenChange, count, limit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Lock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <DialogTitle className="text-xl">Save Limit Reached</DialogTitle>
          </div>
          <DialogDescription className="text-left">
            You've saved {count} of {limit} cases on the free tier.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Upgrade to Researcher or Investigator to save unlimited cases and access advanced features.
          </p>

          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Crown className="w-4 h-4 text-orange-500" />
              <span className="font-medium">Researcher Plan Benefits:</span>
            </div>
            <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1 ml-6">
              <li>• Unlimited saved cases</li>
              <li>• Unlimited match viewing</li>
              <li>• Case comparison tools</li>
              <li>• Advanced filters & map analysis</li>
            </ul>
          </div>

          <div className="flex flex-col gap-2">
            <Link to="/pricing" onClick={() => onOpenChange(false)}>
              <Button className="w-full bg-orange-500 hover:bg-orange-600">
                Upgrade Now
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Maybe Later
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SaveCaseButton;
